/**
 * Микро-помощник для построения DOM.
 *
 * Фреймворк здесь не нужен: интерфейс — это карта и несколько панелей вокруг
 * неё, состояние живёт в самой карте. Зато нужен способ собирать разметку без
 * простыней из createElement/appendChild и без innerHTML, через который
 * атрибуты объектов из тайлов попали бы на страницу как разметка.
 */

type Child = Node | string | number | null | undefined | false;

interface ElementProps {
  class?: string;
  text?: string;
  html?: never;
  attrs?: Record<string, string | number | boolean | undefined>;
  dataset?: Record<string, string>;
  on?: Partial<Record<keyof HTMLElementEventMap, EventListener>>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElementProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props.class) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;

  for (const [name, value] of Object.entries(props.attrs ?? {})) {
    if (value === undefined || value === false) continue;
    node.setAttribute(name, value === true ? "" : String(value));
  }

  for (const [name, value] of Object.entries(props.dataset ?? {})) {
    node.dataset[name] = value;
  }

  for (const [event, handler] of Object.entries(props.on ?? {})) {
    if (handler) node.addEventListener(event, handler);
  }

  appendChildren(node, children);
  return node;
}

export function appendChildren(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "object" ? child : document.createTextNode(String(child)));
  }
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function requireElement<T extends HTMLElement>(selector: string): T {
  const node = document.querySelector<T>(selector);
  if (!node) throw new Error(`В разметке нет обязательного элемента: ${selector}`);
  return node;
}
